import * as THREE from "three";
import Engine from './Engine';
import Utils from './Utilities';
import ScopeCamera from './Camera';
import DefaultSceneManager from './DefaultSceneManager';
import TransformUtils from './utilities/TransformUtils';
import SceneCameraController from "./SceneCameraController";
import SceneObjectUtils from "./utilities/SceneObjectUtils";
import { CustomEventTypes } from '../globals';

const interactiveCameraWorldPosition = new THREE.Vector3();
const interactiveCameraWorldRotation = new THREE.Quaternion();
const cameraForward = new THREE.Vector3();
const lastFrameWorldRotation = new THREE.Quaternion();
const tempEuler = new THREE.Euler();

export default class InteractiveModeManager
{
	initialCameraPosition: THREE.Vector3;
	initialCameraAnchor: THREE.Vector3;
	capturedInitialCameraPosition: boolean;
	constructor()
	{
		this.initialCameraPosition = new THREE.Vector3();
		this.initialCameraAnchor = new THREE.Vector3();
		this.capturedInitialCameraPosition = false;

		document.addEventListener(CustomEventTypes.simulationStarted, this.onSimulationStarted);
		document.addEventListener(CustomEventTypes.simulationStopped, this.onSimulationStopped);
	}

	onSimulationStarted = () => 
	{
		const mainSceneView = Engine.getInstance().getMainSceneView();
		if(!mainSceneView) return;

		const activeCamera = mainSceneView.activeCamera;
		const cameraController = mainSceneView.cameraController;
		this.initialCameraPosition.copy(activeCamera.position);
		this.initialCameraAnchor.copy(cameraController.anchor);
		this.capturedInitialCameraPosition = true;

		const interactiveModeCamera = ScopeCamera.getInteractiveModeCamera(Engine.getInstance().getCurrentScene()!);
		if (interactiveModeCamera)
		{
			this.setCameraAsTarget(cameraController, interactiveModeCamera);
		}
	}

	onSimulationStopped = () => 
	{
		const mainSceneView = Engine.getInstance().getMainSceneView();
		if(!mainSceneView) return;

		const activeCamera = mainSceneView.activeCamera;
		const cameraController = mainSceneView.cameraController;
		if(this.capturedInitialCameraPosition)
		{
			activeCamera.position.copy(this.initialCameraPosition);
			cameraController.anchor.copy(this.initialCameraAnchor);
			this.capturedInitialCameraPosition = false;
		}
		else
		{
			cameraController.goHome();
		}
	}

	onSceneChange(scene)
	{
		if (!Utils.IsSimulationRunning()) return;

		const interactiveModeCamera = ScopeCamera.getInteractiveModeCamera(scene);
		if (interactiveModeCamera)
		{
			const mainSceneView = Engine.getInstance().getMainSceneView();
			if(!mainSceneView) return;
			
			const cameraController = mainSceneView.cameraController;
			Engine.getInstance().updateFocusObject(scene);
			this.setCameraAsTarget(cameraController, interactiveModeCamera);
		}
	}

	setCameraAsTarget(cameraController: SceneCameraController, interactiveModeCamera: ScopeCamera)
	{
		this.undoCameraRoll(interactiveModeCamera);

		interactiveModeCamera.getWorldPosition(interactiveCameraWorldPosition);
		interactiveModeCamera.getWorldQuaternion(interactiveCameraWorldRotation);
		const updateAnchor = cameraController.setTransformAnimation(interactiveCameraWorldPosition, interactiveCameraWorldRotation, false);


		if (updateAnchor)
		{
			this.calcAnchorPoint(cameraController, interactiveModeCamera);
		}
	}

	calcAnchorPoint(cameraController: SceneCameraController, interactiveModeCamera: ScopeCamera)
	{
		interactiveModeCamera.perspectiveCamera.getWorldDirection(cameraForward);

		const visibleMeshes = SceneObjectUtils.getVisibleMeshes();
		const center = SceneObjectUtils.calculateBoundsCenter(visibleMeshes);

		const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(cameraForward.clone().multiplyScalar(-1), center);
		const ray = new THREE.Ray(interactiveCameraWorldPosition, cameraForward);
		const intersection = ray.intersectPlane(plane, new THREE.Vector3());
		if (intersection)
		{
			cameraController.anchor.copy(intersection);
		}
		else
		{
			cameraController.anchor.addVectors(interactiveCameraWorldPosition, cameraForward);
		}
	}

	update()
	{
		const interactiveModeCamera = ScopeCamera.getInteractiveModeCamera(Engine.getInstance().getCurrentScene()!);
		if (interactiveModeCamera)
		{
			interactiveModeCamera.getWorldQuaternion(interactiveCameraWorldRotation);
			if (!interactiveCameraWorldRotation.equals(lastFrameWorldRotation)) this.undoCameraRoll(interactiveModeCamera);
			interactiveModeCamera.getWorldQuaternion(lastFrameWorldRotation);
		}
	}

	undoCameraRoll(interactiveModeCamera)
	{
		interactiveModeCamera.getWorldQuaternion(interactiveCameraWorldRotation);
		tempEuler.setFromQuaternion(interactiveCameraWorldRotation, "YXZ");
		tempEuler.set(tempEuler.x, tempEuler.y, 0, "YXZ");
		interactiveCameraWorldRotation.setFromEuler(tempEuler);

		TransformUtils.setWorldQuaternion(interactiveModeCamera, interactiveCameraWorldRotation);

		if (DefaultSceneManager.getInstance().isDefaultObject(interactiveModeCamera.userData.guid))
		{
			const linkedCameras = DefaultSceneManager.getInstance().getLinkedObjects(interactiveModeCamera);
			for (const linkedCamera of linkedCameras)
			{
				this.undoCameraRoll(linkedCamera);
			}
		}

		if (Engine.getInstance().getPickingManager().isObjectSelected(interactiveModeCamera))
		{
			document.dispatchEvent(new CustomEvent(CustomEventTypes.selectedObjectRotationChanged));
		}
	}
}
